/**
 * Video mute toggle — floating speaker icon overlaid on autoplay-muted product gallery videos.
 *
 * The product gallery hides the native browser chrome when videos autoplay muted (see
 * snippets/product-gallery.liquid). This element provides the only remaining UI: a single
 * button that flips video.muted on click, swaps between muted/unmuted icons, and keeps
 * aria-pressed / aria-label in sync for screen readers.
 *
 * Markup is rendered from snippets/media.liquid only when `muted: true` is passed.
 */
class VideoMuteToggle extends HTMLElement {
  static SELECTORS = {
    button: 'button',
    video: 'video',
    iconMuted: '.video-mute-toggle__icon--muted',
    iconUnmuted: '.video-mute-toggle__icon--unmuted',
    videoMediaParent: 'video-media'
  };
  static ATTRS = {
    pressed: 'aria-pressed',
    label: 'aria-label',
    hidden: 'hidden'
  };
  static EVENTS = {
    click: 'click',
    volumeChange: 'volumechange'
  };

  connectedCallback () {
    this._abort = new AbortController();
    this._button = this.querySelector(VideoMuteToggle.SELECTORS.button);
    this._video = this.closest(VideoMuteToggle.SELECTORS.videoMediaParent)?.querySelector(VideoMuteToggle.SELECTORS.video);
    this._iconMuted = this.querySelector(VideoMuteToggle.SELECTORS.iconMuted);
    this._iconUnmuted = this.querySelector(VideoMuteToggle.SELECTORS.iconUnmuted);
    this._labelMute = this.dataset.labelMute || '';
    this._labelUnmute = this.dataset.labelUnmute || '';

    if (!this._button || !this._video) return;

    this._button.addEventListener(VideoMuteToggle.EVENTS.click, () => this._toggle(), { signal: this._abort.signal });
    this._video.addEventListener(VideoMuteToggle.EVENTS.volumeChange, () => this._syncState(), { signal: this._abort.signal });

    this._syncState();
  }

  disconnectedCallback () {
    this._abort?.abort();
  }

  _toggle () {
    this._video.muted = !this._video.muted;
  }

  _syncState () {
    const muted = this._video.muted;
    this._button.setAttribute(VideoMuteToggle.ATTRS.pressed, muted ? 'true' : 'false');
    this._button.setAttribute(VideoMuteToggle.ATTRS.label, muted ? this._labelUnmute : this._labelMute);
    this._iconMuted?.toggleAttribute(VideoMuteToggle.ATTRS.hidden, !muted);
    this._iconUnmuted?.toggleAttribute(VideoMuteToggle.ATTRS.hidden, muted);
  }
}

if (!customElements.get('video-mute-toggle')) {
  customElements.define('video-mute-toggle', VideoMuteToggle);
}
